import { defineRouter } from '#q-app';
import { routerAuthNotifyPlugin } from '@0x234/common-vue/plugins/router-auth-notify-plugin';
import routes, { Route } from 'router/routes';
import { strategyConfig } from 'src/configs/strategy-config';
import AppRoute from 'src/router/app-route';
import { useAppStore } from 'stores/app-store';
import { useAuthStore } from 'stores/auth-store';
import { useErrorStore } from 'stores/error-store';
import { clearErrorPage, raiseErrorPage } from 'utils/error-page';
import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory, START_LOCATION } from 'vue-router';

// The header is fixed and 62px tall, so an anchored element is placed clear of it rather than underneath it.
const ANCHOR_OFFSET = 80;
// A page reached by a client-side navigation can render its content a moment after the route resolves.
const ANCHOR_WAIT_ATTEMPTS = 30;
const ANCHOR_WAIT_INTERVAL = 16;

export default defineRouter(({ ssrContext, store }) => {
    const router = createRouter({
        routes,
        history: configureHistoryMode(import.meta.env.QUASAR_VUE_ROUTER_BASE),
        scrollBehavior: resolveScrollPosition
    });
    configureErrorStatusMiddleware(router, { ssrContext, store });
    configureMiddleware(router);
    configureErrorHandler(router);
    routerAuthNotifyPlugin.install(router, {
        isUserAuthenticated: () => useAuthStore().userAuthenticated,
        suppressLoginNotification: true,
        strategies: Object.entries(strategyConfig)
            .filter(([, s]) => s.domain)
            .reduce((reducedStrategies, [id, { name, domain }]) => {
                reducedStrategies[id] = { name, domain };
                return reducedStrategies;
            }, {})
    });
    return router;
});

function configureHistoryMode(routerBase) {
    const historyMode = import.meta.env.QUASAR_SERVER
        ? createMemoryHistory
        : (import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory);
    return historyMode(routerBase);
}

/**
 * Decides where a navigation lands. Back and forward return to where the visitor was. A link to an anchor, such as a
 * figure's numbered marker pointing at its note, lands on that element clear of the fixed header. Anything else starts
 * at the top.
 *
 * @param {import('vue-router').RouteLocationNormalized} to - The route being navigated to.
 * @param {import('vue-router').RouteLocationNormalized} from - The route being navigated away from.
 * @param {Object|null} savedPosition - The position recorded for this entry, present only for back and forward.
 * @returns {Object|Promise<Object>} - The scroll position for the router to apply.
 */
export function resolveScrollPosition(to, from, savedPosition) {
    if (savedPosition) {
        return savedPosition;
    }
    if (to.hash) {
        return findAnchorTarget(to.hash).then(el => el ? { el, top: ANCHOR_OFFSET } : { left: 0, top: 0 });
    }
    return { left: 0, top: 0 };
}

// Looked up by id rather than as a selector, so an anchor that is not a valid CSS selector cannot throw. Timers rather
// than animation frames, because frames stop in a background tab and the promise would never settle.
function findAnchorTarget(hash) {
    let id;
    try {
        id = decodeURIComponent(hash.slice(1));
    } catch {
        return Promise.resolve(null);
    }
    return new Promise(resolve => {
        let attempts = 0;
        const look = () => {
            const el = document.getElementById(id);
            if (el || ++attempts >= ANCHOR_WAIT_ATTEMPTS) {
                resolve(el);
                return;
            }
            setTimeout(look, ANCHOR_WAIT_INTERVAL);
        };
        look();
    });
}

/**
 * Installs the guard that carries a route's terminal status into the response.
 *
 * A route that exists only to say something went wrong names the status it stands for, and this is what turns that
 * into an answer: the status is raised before the page is reached, so the render middleware finds it on the SSR
 * context and the browser hydrates against the same error rather than the page that was never there. It runs ahead
 * of every other guard and clears anything a previous navigation raised, so a page that raises its own status from
 * preFetch, which happens after all guards, always has the last word.
 *
 * The first navigation is the exception, and it has to be. A status a page raised from preFetch belongs to the route
 * it was raised on, not to a route of its own, so nothing in the route table names it and the state boot file is what
 * carries it into the browser. Clearing on the way through that navigation would drop the status the server answered
 * with and leave the browser hydrating the page that was never there.
 *
 * @param {import('vue-router').Router} router - Router instance to guard.
 * @param {Object} [options] - The request the router was created for.
 * @param {Object} [options.ssrContext] - The SSR context, absent when running in the browser.
 * @param {Object} [options.store] - The request's Pinia instance, absent when the active one can be used.
 */
export function configureErrorStatusMiddleware(router, { ssrContext, store } = {}) {
    router.beforeEach((to, from) => {
        useErrorStore(store).beginNavigation(to);
        const statusCode = to.meta?.errorStatusCode ?? null;
        if (statusCode) {
            raiseErrorPage(statusCode, { ssrContext, store });
        } else if (from !== START_LOCATION) {
            clearErrorPage({ ssrContext, store });
        }
    });
}

/**
 * Installs the guard that loads the current user, sends a logged-out visitor away from routes that are not public,
 * and holds routes back by role and profile readiness.
 *
 * @param {import('vue-router').Router} router - Router instance to guard.
 */
export function configureMiddleware(router) {
    router.beforeEach(async to => {
        const authStore = useAuthStore();
        authStore.subscribeToAuthEvents(); // Subscribe to auth events to handle clearing of stores on logout - must be subscribed here.
        await authStore.loadUser();
        if (to.meta['public'] !== true && !authStore.userAuthenticated) {
            return AppRoute.build(Route.Login);
        }
        if (to.meta['roles'] && !to.meta['roles'].includes(authStore.user.role)) {
            return AppRoute.build(Route.Dashboard);
        }
        const profileReadinessRedirect = resolveProfileReadinessRedirect({
            isAuthenticated: authStore.userAuthenticated,
            isReady: authStore.userReady,
            isSetupRoute: to.name === Route.Setup,
            requiresProfile: to.meta['requiresProfile'] === true
        });
        if (profileReadinessRedirect) {
            return profileReadinessRedirect;
        }
        return true;
    });
}

export function resolveProfileReadinessRedirect({ isAuthenticated, isReady, isSetupRoute = false, requiresProfile }) {
    if (!isAuthenticated) {
        return null;
    }
    if (!isReady && requiresProfile && !isSetupRoute) {
        return AppRoute.build(Route.Setup);
    }
    if (isReady && isSetupRoute) {
        return AppRoute.build(Route.ManageUserProfile);
    }
    return null;
}

function configureErrorHandler(router) {
    router.onError(async err => {
        if (err.message.includes('error loading dynamically imported module')
            || err.message.includes('404')
            || (err.response && err.response.status === 404)) {
            try {
                const response = await fetch('/status');
                if (response.ok) {
                    useAppStore().newVersionServiceDialog = {};
                    console.warn('A new version of the application is available. Please refresh your browser.');
                    return;
                }
                useAppStore().unavailableServiceDialog = { type: 'client' };
                console.error('Server is responding but not OK:', response.statusText);
            } catch (err) {
                useAppStore().unavailableServiceDialog = { type: 'client' };
                console.error('Server seems to be unavailable:', err);
            }
        }
    });
}
